import { graphJson, type GraphRequestOptions } from "./client.ts";

/**
 * Paging over Graph collections (ARCHITECTURE.md §8).
 *
 * Mail, calendar, contacts and OneDrive all support /delta. The deltaLink
 * arrives only on the final page, so it is surfaced through a callback and
 * persisted to the `cursors` store — a repeat export then fetches only what
 * changed.
 */

export interface GraphCollection<T> {
    value: T[];
    "@odata.nextLink"?: string;
    "@odata.deltaLink"?: string;
}

export interface PagingOptions extends GraphRequestOptions {
    /** Fires on the final page of a delta query. Persist this to resume cheaply. */
    onDeltaLink?: (deltaLink: string) => void;
    /**
     * Fires with each nextLink before it is followed. Checkpointing here means
     * an interruption resumes at the page boundary rather than the beginning.
     */
    onNextLink?: (nextLink: string) => void;
}

/** Yields one page at a time. */
export async function* pages<T>(
    startUrl: string,
    options: PagingOptions
): AsyncGenerator<T[], void, undefined> {
    let url: string | undefined = startUrl;

    while (url) {
        const page: GraphCollection<T> = await graphJson<GraphCollection<T>>(url, options);
        yield page.value;

        const deltaLink = page["@odata.deltaLink"];
        if (deltaLink) {
            options.onDeltaLink?.(deltaLink);
        }

        url = page["@odata.nextLink"];
        if (url) {
            options.onNextLink?.(url);
        }
    }
}

/** Yields individual items, flattening pages. */
export async function* iterate<T>(
    startUrl: string,
    options: PagingOptions
): AsyncGenerator<T, void, undefined> {
    for await (const page of pages<T>(startUrl, options)) {
        yield* page;
    }
}

/**
 * Materialises every page into one array.
 *
 * Only for collections known to be small — folder lists, calendars, To Do
 * lists. Never for messages or drive items; use iterate() so memory stays flat.
 */
export async function collect<T>(startUrl: string, options: PagingOptions): Promise<T[]> {
    const all: T[] = [];
    for await (const page of pages<T>(startUrl, options)) {
        all.push(...page);
    }
    return all;
}
